import { useState, useEffect } from 'react';
import { useModel } from '@umijs/max';
import { SupabaseAuth } from '@/utils/supabase';
import { message } from 'antd';
import type { User as SupabaseUser } from '@supabase/supabase-js';

export interface LoginResponse {
  success: boolean;
  message?: string;
  data?: any;
}

export interface UseAuthReturn {
  // State
  user: SupabaseUser | null;
  isLoggedIn: boolean;
  loading: boolean;
  
  // Actions
  login: (email: string, password: string) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  updateUser: (attributes: any) => Promise<boolean>;
}

export const useAuth = (): UseAuthReturn => {
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const { refresh } = useModel('@@initialState');

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Get current session from Supabase
        const { session, error } = await SupabaseAuth.getSession();
        
        if (error) {
          console.error('Get session error:', error);
          setUser(null);
          setIsLoggedIn(false);
        } else if (session) {
          setUser(session.user);
          setIsLoggedIn(true);
        } else {
          setUser(null);
          setIsLoggedIn(false);
        }
      } catch (error) {
        console.error('Init auth error:', error);
        setUser(null);
        setIsLoggedIn(false);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Listen to auth state changes
    const { data: { subscription } } = SupabaseAuth.onAuthStateChange((event, session) => {
      console.log('Auth state changed:', event);
      if (session) {
        setUser(session.user);
        setIsLoggedIn(true);
      } else {
        setUser(null);
        setIsLoggedIn(false);
      }
      refresh();
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, [refresh]);

  // Login function
  const login = async (email: string, password: string): Promise<LoginResponse> => {
    setLoading(true);
    try {
      // Use authService which handles both auth and public.users fetch
      const { authService } = await import('@/services/supabase/authService');
      const response = await authService.login({ email, password });
      
      // Check if email verification is required
      if ((response as any).requiresVerification) {
        setLoading(false);
        return {
          success: false,
          message: response.message,
          ...(response as any), // Include requiresVerification, email, userId
        };
      }
      
      if (!response.success) {
        message.error(response.message || 'Đăng nhập thất bại');
        return {
          success: false,
          message: response.message,
        };
      }
      
      if (response.authUser) {
        setUser(response.authUser);
        setIsLoggedIn(true);
        await refresh();
        
        message.success('Đăng nhập thành công!');
        return {
          success: true,
          data: response.data, // Full user data with permissions from public.users
        };
      }
      
      return {
        success: false,
        message: 'Đăng nhập thất bại',
      };
    } catch (error: any) {
      console.error('Login error:', error);
      message.error(error.message || 'Đã có lỗi xảy ra');
      return {
        success: false,
        message: error.message || 'Đã có lỗi xảy ra',
      };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    setLoading(true);
    try {
      await SupabaseAuth.signOut();
      setUser(null);
      setIsLoggedIn(false);
      refresh();
      message.success('Đăng xuất thành công!');
    } catch (error) {
      console.error('Logout error:', error);
      message.error('Đăng xuất thất bại');
    } finally {
      setLoading(false);
    }
  };

  // Update user function
  const updateUser = async (attributes: any): Promise<boolean> => {
    try {
      const { data, error } = await SupabaseAuth.updateUser(attributes);
      
      if (error) {
        message.error(error.message || 'Cập nhật thất bại');
        return false;
      }
      
      if (data?.user) {
        setUser(data.user);
        refresh();
        message.success('Cập nhật thành công!');
        return true;
      }
      
      return false;
    } catch (error: any) {
      console.error('Update user error:', error);
      message.error(error.message || 'Đã có lỗi xảy ra');
      return false;
    }
  };

  return {
    // State
    user,
    isLoggedIn,
    loading,
    
    // Actions
    login,
    logout,
    updateUser,
  };
};

export default useAuth;